/**
 * The one guard on a post-sign-in destination, shared by the client's
 * /login and /auth/return (src/client/lib/auth-return.ts) and the server's
 * preview claim (#5250, src/server/routes/auth-preview.ts), so the two can
 * never disagree about what counts as an internal path.
 */
const RETURN_TO_BASE = "https://crosbe.invalid";
const AUTH_FLOW_PATHS = ["/welcome", "/login", "/auth/return", "/api/auth", "/api/auth-preview"];

/**
 * Accept only an internal app URL. Parsing against a fixed origin rejects
 * protocol-relative and absolute URLs without tying the helper to one deploy
 * host (prod, staging, previews and localhost all use the same client build).
 */
export function safeReturnTo(candidate: string | null | undefined): string {
  if (!candidate?.startsWith("/")) return "/";

  try {
    const parsed = new URL(candidate, RETURN_TO_BASE);
    if (parsed.origin !== RETURN_TO_BASE) return "/";
    if (AUTH_FLOW_PATHS.some((path) =>
      parsed.pathname === path || parsed.pathname.startsWith(`${path}/`)
    )) {
      return "/";
    }
    const out = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    // #5250 round 2: the parser normalises "/a/..//x", "/.//x" and "/.\\x"
    // to a pathname of "//x" while the origin still matches, and a browser
    // reads "//x" as another host. Only one "/" may lead the final string.
    return isSingleSlashPath(out) ? out : "/";
  } catch {
    return "/";
  }
}

/** Starts with exactly one "/" and no second "/" or "\" straight after it. */
function isSingleSlashPath(path: string): boolean {
  return path.startsWith("/") && path[1] !== "/" && path[1] !== "\\";
}
